import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { MetaHead } from '../components/MetaHead';
import { GalleryModal } from '../components/GalleryModal';
import { COMPANY_INFO, SCRAPED_PAGES, COMMON_CONTACT_CALLOUT } from '../data/scrapedData';

export function Home() {
  const p = SCRAPED_PAGES.index;
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(0);

  // Form state
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  const homeImages = [
    "https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=85,format=auto/assets/caa9ce5a-9b51-4c31-bd1f-3a73d776d9c2/hvidmalet-bygning-med-graesplaene-og-hegn.jpg",
    "https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=85,format=auto/assets/caa9ce5a-9b51-4c31-bd1f-3a73d776d9c2/traeterrasse-med-liggestole-foran-traehytte.jpg",
    "https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=85,format=auto/assets/caa9ce5a-9b51-4c31-bd1f-3a73d776d9c2/madlavning-ved-stort-komfur-i-industrikoekken.jpg",
    "https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=85,format=auto/assets/caa9ce5a-9b51-4c31-bd1f-3a73d776d9c2/faellesspisning-med-buffet-og-tallerkener.jpg"
  ];

  // Core treatments matching the dropdown and services
  const dropdownTreatments = [
    {
      title: "Døgnbehandling",
      path: "/doegnbehandling",
      eyebrow: "DØGNBEHANDLING",
      desc: "Helhedsorienteret misbrugsbehandling med ro og struktur i rolige rammer i Sejstrup for voksne fra hele landet."
    },
    {
      title: "Afgiftning",
      path: "/behandling/afgiftning",
      eyebrow: "SKÅNSOM AFGIFTNING",
      desc: "Det første skridt mod en stoffri tilværelse i trygge rammer med lægelig supervision og NADA-øreakupunktur."
    },
    {
      title: "Dobbeltdiagnose",
      path: "/dobbeltdiagnose",
      eyebrow: "DOBBELTDIAGNOSE",
      desc: "Specialiseret behandling ved samspil mellem misbrug og psykiatriske problemstillinger eller komorbiditet."
    },
    {
      title: "Botilbud",
      path: "/botilbud",
      eyebrow: "MIDLERTIDIGT BOTILBUD",
      desc: "Trygge boligrammer og individuelt tilrettelagt støtte ved misbrug, psykisk sårbarhed og sociale udfordringer."
    }
  ];

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setFormError('');

    const form = e.target;
    const formData = new FormData(form);
    const data = Object.fromEntries(formData.entries());

    try {
      const response = await fetch('/_api/webform/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });

      if (response.ok) {
        setFormSubmitted(true);
        form.reset();
      } else {
        setFormError('Der opstod en fejl under afsendelsen. Prøv venligst igen.');
      }
    } catch {
      setFormError('Der opstod en netværksfejl. Ring venligst på 75 17 20 80.');
    } finally {
      setSubmitting(false);
    }
  };

  const pageTitle = "Misbrugsbehandling der ser hele mennesket | Midtgaarden";
  const pageDescription = "Få professionel misbrugsbehandling hos Midtgaarden. Individuelt tilrettelagt som dag- eller døgnbehandling - også ved dobbeltdiagnose.";

  return (
    <>
      <MetaHead title={pageTitle} description={pageDescription} />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": pageTitle,
            "description": pageDescription,
            "url": "https://www.midtgaarden.dk/"
          })}
        </script>
      </Helmet>

      {/* HERO SECTION - Focused on Misbrugsbehandling */}
      <section className="relative min-h-[85vh] flex items-center bg-[#1C1C1E] text-white overflow-hidden py-20 sm:py-28">
        {/* Full-bleed background image with dark overlay */}
        <img
          src="https://assets.auxodev.dk/assets/caa9ce5a-9b51-4c31-bd1f-3a73d776d9c2/gaardhave-med-havebassin-og-liggestole.jpg"
          alt="Brolagt gårdhave med anlagt havebassin og liggestole foran hvid bygning"
          className="absolute inset-0 w-full h-full object-cover object-center opacity-75 scale-105 rounded-none"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#1C1C1E]/80 via-[#1C1C1E]/50 to-transparent z-10" />

        <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full [text-shadow:_0_2px_12px_rgba(0,0,0,0.85)]">
          <div className="max-w-3xl space-y-6">
            
            {/* Pill-shaped badge */}
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold bg-[#3885B8] text-white shadow-md">
              <i className="fa-solid fa-heart-pulse text-xs" aria-hidden="true" />
              <span>Misbrugsbehandling med over 20 års erfaring</span>
            </div>

            {/* Oversized display heading focused on Misbrugsbehandling */}
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-5xl font-extrabold text-white tracking-tight leading-[1.15]">
              Professionel misbrugsbehandling med blik for hele mennesket
            </h1>

            {/* Constrained-width subheading paragraph */}
            <p className="text-base sm:text-lg md:text-xl text-white/95 leading-relaxed font-normal max-w-2xl pt-2">
              Hos Midtgaarden får du professionel misbrugsbehandling, der tager udgangspunkt i hele din situation. Vi hjælper mennesker med stofmisbrug, alkoholmisbrug og sammensatte problemstillinger gennem individuelt tilrettelagte forløb i rolige rammer i Sejstrup nær Esbjerg.
            </p>

            {/* Primary CTA button group */}
            <div className="pt-4 flex items-center gap-4 flex-wrap">
              <a
                href={COMPANY_INFO.phoneTel}
                className="h-14 px-8 rounded-full bg-[#3885B8] hover:bg-[#2A6E9B] active:bg-[#1F557B] text-white font-bold text-base shadow-lg transition-all duration-200 inline-flex items-center gap-3 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#3885B8]"
              >
                <i className="fa-solid fa-phone text-base" aria-hidden="true" />
                <span className="whitespace-nowrap">Ring: {COMPANY_INFO.phone}</span>
              </a>

              <a
                href={COMPANY_INFO.emailMailto}
                aria-label="Send e-mail til Midtgaarden"
                className="w-14 h-14 rounded-full bg-white/20 hover:bg-white/30 backdrop-blur-sm border border-white/30 text-white flex items-center justify-center transition-all duration-200 shadow-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white shrink-0"
              >
                <i className="fa-solid fa-envelope text-lg" aria-hidden="true" />
              </a>
            </div>

            {/* Stats row at bottom left */}
            <div className="pt-10 sm:pt-14 mt-8 border-t border-white/20 grid grid-cols-3 gap-4 sm:gap-8 max-w-2xl">
              <div className="pr-4 sm:pr-6 border-r border-white/20">
                <div className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight">20+</div>
                <div className="text-xs sm:text-sm text-white/80 mt-1 font-medium">Års erfaring</div>
              </div>

              <div className="px-2 sm:px-6 border-r border-white/20">
                <div className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight">18</div>
                <div className="text-xs sm:text-sm text-white/80 mt-1 font-medium">Pladser i Sejstrup</div>
              </div>

              <div className="pl-2 sm:pl-6">
                <div className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight">24/7</div>
                <div className="text-xs sm:text-sm text-white/80 mt-1 font-medium">Døgndækning</div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* OVERLAPPING 3-CARD INFO BAR */}
      <section className="relative z-30 -mt-10 sm:-mt-14 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          
          {/* Card 1: Telefonisk kontakt */}
          <div className="bg-[#F1F5F9] text-[#1C1C1E] p-6 sm:p-7 rounded-none shadow-xl border border-[#1C1C1E]/10 space-y-3 flex flex-col justify-between">
            <div className="flex items-start justify-between">
              <div className="w-12 h-12 rounded-none bg-[#3885B8] text-white flex items-center justify-center text-xl">
                <i className="fa-solid fa-phone" aria-hidden="true" />
              </div>
              <span className="text-[10px] uppercase tracking-wider font-semibold text-[#1C1C1E]/60 bg-[#1C1C1E]/5 px-2.5 py-1 rounded-none">
                Døgndækket
              </span>
            </div>
            <div className="space-y-1 pt-2">
              <h3 className="text-lg font-bold text-[#1C1C1E]">Telefonisk kontakt</h3>
              <p className="text-xs text-[#1C1C1E]/70">Du kan altid ringe til os for visitation eller rådgivning om misbrugsbehandling.</p>
            </div>
            <a
              href={COMPANY_INFO.phoneTel}
              className="inline-flex items-center gap-2 text-sm font-bold text-[#3885B8] hover:underline transition-colors pt-2 border-t border-[#1C1C1E]/10"
            >
              <span>{COMPANY_INFO.phone}</span>
              <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
            </a>
          </div>

          {/* Card 2: E-mail */}
          <div className="bg-[#EDF2F7] text-[#1C1C1E] p-6 sm:p-7 rounded-none shadow-xl border border-[#1C1C1E]/10 space-y-3 flex flex-col justify-between">
            <div className="flex items-start justify-between">
              <div className="w-12 h-12 rounded-none bg-[#3885B8] text-white flex items-center justify-center text-xl">
                <i className="fa-solid fa-envelope" aria-hidden="true" />
              </div>
              <span className="text-[10px] uppercase tracking-wider font-semibold text-[#1C1C1E]/60 bg-[#1C1C1E]/5 px-2.5 py-1 rounded-none">
                E-mail
              </span>
            </div>
            <div className="space-y-1 pt-2">
              <h3 className="text-lg font-bold text-[#1C1C1E]">Skriv til os</h3>
              <p className="text-xs text-[#1C1C1E]/70">Vi besvarer alle henvendelser hurtigt, fortroligt og uforpligtende.</p>
            </div>
            <a
              href={COMPANY_INFO.emailMailto}
              className="inline-flex items-center gap-2 text-sm font-bold text-[#3885B8] hover:underline transition-all pt-2 border-t border-[#1C1C1E]/10"
            >
              <span>{COMPANY_INFO.email}</span>
              <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
            </a>
          </div>

          {/* Card 3: Godkendelser */}
          <div className="bg-[#E2E8F0] text-[#1C1C1E] p-6 sm:p-7 rounded-none shadow-xl border border-[#1C1C1E]/10 space-y-3 flex flex-col justify-between">
            <div className="flex items-start justify-between">
              <div className="w-12 h-12 rounded-none bg-[#1C1C1E]/10 text-[#1C1C1E] flex items-center justify-center text-xl">
                <i className="fa-solid fa-shield-halved" aria-hidden="true" />
              </div>
              <span className="text-[10px] uppercase tracking-wider font-semibold text-[#1C1C1E]/60 bg-[#1C1C1E]/5 px-2.5 py-1 rounded-none">
                Godkendelser
              </span>
            </div>
            <div className="space-y-1 pt-2">
              <h3 className="text-lg font-bold text-[#1C1C1E]">Godkendt tilbud</h3>
              <p className="text-xs text-[#1C1C1E]/70">SEL § 101, § 107 og SUL § 141 – Tilsyn ved Socialtilsyn Syd.</p>
            </div>
            <Link
              to="/tilsyn"
              className="inline-flex items-center gap-2 text-sm font-bold text-[#3885B8] hover:underline pt-2 border-t border-[#1C1C1E]/10"
            >
              <span>Se godkendelser og tilsyn</span>
              <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
            </Link>
          </div>

        </div>
      </section>

      {/* OM MIDTGAARDEN & MISBRUGSCENTER MED OVER 20 ÅRS ERFARING */}
      <section className="py-20 sm:py-28 bg-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-stretch">
            
            {/* Left Column: Text Content */}
            <div className="lg:col-span-7 space-y-6 flex flex-col justify-between">
              <div className="space-y-6">
                {/* Eyebrow label */}
                <div className="flex items-center gap-3">
                  <div className="w-8 h-[1px] bg-[#3885B8]" />
                  <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#3885B8]">
                    OM MIDTGAARDEN
                  </span>
                </div>

                {/* Heading h2 from paste */}
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#1C1C1E] tracking-tight mb-4">
                  Et misbrugscenter med mere end 20 års erfaring
                </h2>

                {/* Lead quote */}
                <div className="text-4xl text-[#3885B8] pt-2">
                  <i className="fa-solid fa-quote-left" aria-hidden="true" />
                </div>

                <p className="text-lg text-[#1C1C1E] font-medium leading-relaxed italic">
                  "Med mere end 20 års erfaring leverer Midtgaarden kvalificeret misbrugsbehandling til mennesker med misbrug af både stoffer og alkohol. Vi arbejder med helheden i mennesket."
                </p>

                {/* Supporting body text from paste and existing introText */}
                <div className="space-y-4 text-[#1C1C1E]/80 text-sm sm:text-base leading-relaxed">
                  <p>
                    Som misbrugscenter har vi mere end 20 års erfaring med at støtte voksne på vej mod en mere stabil hverdag. Vi modtager borgere fra hele landet og tilpasser indsatsen til den enkeltes fysiske, psykiske og sociale forhold. Målet er at skabe sammenhæng frem for løsninger i enkelte dele.
                  </p>
                  <p>
                    Et behandlingsforløb kan omfatte{' '}
                    <Link
                      to="/behandling/afgiftning"
                      className="font-medium text-[#3885B8] underline decoration-[#3885B8]/40 hover:decoration-[#3885B8] hover:text-[#2A6E9B] transition-colors"
                    >
                      afgiftning
                    </Link>
                    , primærbehandling, alkoholbehandling, tilbagefaldsforebyggelse, efterværn og udslusning. Det er ikke alle, der har brug for det samme. Derfor ser vi på behov, ressourcer og udfordringer, før rammerne for forløbet bliver lagt.
                  </p>
                  <p className="text-[#1C1C1E]/70 pt-1">
                    Med vores brede vifte af{' '}
                    <Link
                      to="/behandling"
                      className="font-medium text-[#3885B8] underline decoration-[#3885B8]/40 hover:decoration-[#3885B8] hover:text-[#2A6E9B] transition-colors"
                    >
                      behandlingstilbud
                    </Link>{' '}
                    og differentierede forløb er Midtgaarden kendt for at lave individuelt tilrettelagte forløb og for at tilbyde en helhedsorienteret indsats, der er skræddersyet den enkelte.
                  </p>
                </div>
              </div>
            </div>

            {/* Right Column: Image with badge */}
            <div className="lg:col-span-5 relative flex flex-col h-full">
              <div className="relative rounded-none overflow-hidden shadow-2xl border border-[#1C1C1E]/10 flex-1 min-h-[420px]">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=90,format=auto/assets/caa9ce5a-9b51-4c31-bd1f-3a73d776d9c2/traeterrasse-med-liggestole-foran-traehytte.jpg"
                  alt="Solrig træterrasse med liggestole og havemøbler foran træhytte"
                  className="w-full h-full object-cover rounded-none"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1C1C1E]/60 via-transparent to-transparent" />
              </div>

              {/* Far right score/rating badge */}
              <div className="absolute -bottom-6 -right-2 sm:-right-6 bg-white p-4 rounded-none shadow-2xl border border-[#1C1C1E]/10 flex items-center gap-3 z-10">
                <div className="text-[#3885B8] text-2xl font-black">20+</div>
                <div className="border-l border-[#1C1C1E]/10 pl-3 text-xs text-[#1C1C1E]">
                  <div className="flex text-amber-400 gap-0.5 mb-0.5">
                    <i className="fa-solid fa-star" />
                    <i className="fa-solid fa-star" />
                    <i className="fa-solid fa-star" />
                    <i className="fa-solid fa-star" />
                    <i className="fa-solid fa-star" />
                  </div>
                  <span className="font-bold block text-[#1C1C1E]">Godkendt af Socialtilsyn Syd</span>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* HVILKEN MISBRUGSBEHANDLING PASSER TIL BEHOVET - New Dedicated Section */}
      <section className="py-20 sm:py-28 bg-[#F8FAFC] border-t border-b border-[#1C1C1E]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="max-w-3xl mb-14 space-y-4">
            <span className="text-xs uppercase tracking-[0.2em] font-extrabold text-[#3885B8] block">
              INDIVIDUEL TILPASNING
            </span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#1C1C1E] tracking-tight mb-4">
              Hvilken misbrugsbehandling passer til behovet
            </h2>
            <div className="w-16 h-1 bg-[#3885B8]" />
            <p className="text-base sm:text-lg text-[#1C1C1E]/80 leading-relaxed pt-2">
              Den bedste form for misbrugsbehandling afhænger af hverdagen, misbrugets karakter og eventuelle samtidige vanskeligheder. Hos os kan indsatsen foregå som døgnbehandling eller dagbehandling. Vi kan også tilbyde et midlertidigt{' '}
              <Link
                to="/botilbud"
                className="font-medium text-[#3885B8] underline decoration-[#3885B8]/40 hover:decoration-[#3885B8] hover:text-[#2A6E9B] transition-colors"
              >
                botilbud
              </Link>
              , når der er behov for støtte omkring hverdagen.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 items-stretch">
            
            {/* Card 1: Døgnbehandling */}
            <div className="bg-white p-8 rounded-none border border-[#1C1C1E]/10 shadow-xs flex flex-col justify-between hover:shadow-md transition-shadow">
              <div className="space-y-4">
                <div className="w-12 h-12 rounded-none bg-[#3885B8]/10 text-[#3885B8] flex items-center justify-center text-xl">
                  <i className="fa-solid fa-bed" aria-hidden="true" />
                </div>
                <span className="text-xs font-extrabold uppercase tracking-widest text-[#3885B8] block">
                  Døgnbehandling
                </span>
                <h3 className="text-xl font-bold text-[#1C1C1E] leading-snug">
                  Misbrugsbehandling som døgnbehandling giver ro til forandring
                </h3>
                <p className="text-sm text-[#1C1C1E]/75 leading-relaxed">
                  Misbrugsbehandling som døgnbehandling giver mulighed for afstand til vante mønstre og tid til at arbejde koncentreret med forandring. Tilbuddet henvender sig til voksne fra 18 år. Under opholdet indgår behandlingen i en struktureret hverdag med støtte til den enkeltes mål.
                </p>
              </div>

              <div className="pt-6 mt-6 border-t border-[#1C1C1E]/10">
                <Link
                  to="/doegnbehandling"
                  className="inline-flex items-center gap-2 text-sm font-bold text-[#3885B8] hover:underline"
                >
                  <span>Læs om døgnbehandling</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                </Link>
              </div>
            </div>

            {/* Card 2: Dagbehandling */}
            <div className="bg-white p-8 rounded-none border border-[#1C1C1E]/10 shadow-xs flex flex-col justify-between hover:shadow-md transition-shadow">
              <div className="space-y-4">
                <div className="w-12 h-12 rounded-none bg-[#3885B8]/10 text-[#3885B8] flex items-center justify-center text-xl">
                  <i className="fa-solid fa-calendar-day" aria-hidden="true" />
                </div>
                <span className="text-xs font-extrabold uppercase tracking-widest text-[#3885B8] block">
                  Dagbehandling
                </span>
                <h3 className="text-xl font-bold text-[#1C1C1E] leading-snug">
                  Dagbehandling med plads til hverdagen
                </h3>
                <p className="text-sm text-[#1C1C1E]/75 leading-relaxed">
                  Dagbehandling kan være relevant, når du kan bo hjemme og samtidig har behov for en intensiv og fast indsats i dagtimerne. Valget mellem dagbehandling og døgnbehandling træffes ud fra en konkret vurdering. Vi hjælper med at afklare, hvilken ramme der bedst matcher situationen.
                </p>
              </div>

              <div className="pt-6 mt-6 border-t border-[#1C1C1E]/10">
                <Link
                  to="/behandling/dagbehandling"
                  className="inline-flex items-center gap-2 text-sm font-bold text-[#3885B8] hover:underline"
                >
                  <span>Læs om dagbehandling</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                </Link>
              </div>
            </div>

            {/* Card 3: Botilbud */}
            <div className="bg-white p-8 rounded-none border border-[#1C1C1E]/10 shadow-xs flex flex-col justify-between hover:shadow-md transition-shadow">
              <div className="space-y-4">
                <div className="w-12 h-12 rounded-none bg-[#3885B8]/10 text-[#3885B8] flex items-center justify-center text-xl">
                  <i className="fa-solid fa-house-chimney" aria-hidden="true" />
                </div>
                <span className="text-xs font-extrabold uppercase tracking-widest text-[#3885B8] block">
                  Botilbud
                </span>
                <h3 className="text-xl font-bold text-[#1C1C1E] leading-snug">
                  Midlertidigt botilbud med trygge rammer
                </h3>
                <p className="text-sm text-[#1C1C1E]/75 leading-relaxed">
                  Et botilbud ved misbrug skal skabe rammer, hvor forandring kan blive en del af hverdagen. Hos Midtgaarden kan opholdet indgå i et individuelt forløb med støtte omkring misbrug, psykisk sårbarhed og sociale udfordringer i rolige rammer i Sejstrup.
                </p>
              </div>

              <div className="pt-6 mt-6 border-t border-[#1C1C1E]/10">
                <Link
                  to="/botilbud"
                  className="inline-flex items-center gap-2 text-sm font-bold text-[#3885B8] hover:underline"
                >
                  <span>Læs om botilbud</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                </Link>
              </div>
            </div>

          </div>

        </div>
      </section>

      {/* INDSATSER & FORLØB - Central oversigt over vores tilbud */}
      <section className="py-20 sm:py-28 bg-white border-b border-[#1C1C1E]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Left-aligned top header block */}
          <div className="max-w-3xl mb-14">
            <span className="text-xs uppercase tracking-[0.2em] font-extrabold text-[#3885B8] block mb-2">
              INDSATSER & FORLØB
            </span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#1C1C1E] tracking-tight mb-4">
              Central oversigt over vores tilbud
            </h2>
            <div className="w-16 h-1 bg-[#3885B8] my-5" />
            <p className="text-sm sm:text-base text-[#1C1C1E]/70 leading-relaxed">
              Midtgaarden tilbyder specialiserede forløb opbygget omkring borgerens individuelle behov, faglige metoder og personlige ressourcer.
            </p>
          </div>

          {/* Grid with items featuring left borders */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {dropdownTreatments.map((item) => (
              <div
                key={item.title}
                className="border-l-2 border-[#3885B8] pl-6 py-2 flex flex-col justify-between space-y-4"
              >
                <div className="space-y-2">
                  <span className="text-[11px] font-extrabold uppercase tracking-widest text-[#3885B8] block">
                    {item.eyebrow}
                  </span>
                  <h3 className="text-lg font-bold text-[#1C1C1E] uppercase tracking-wide">
                    {item.title}
                  </h3>
                  <p className="text-xs sm:text-sm text-[#1C1C1E]/70 leading-relaxed pt-1">
                    {item.desc}
                  </p>
                </div>

                <Link
                  to={item.path}
                  className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#3885B8] hover:underline pt-2"
                >
                  <span>Læs mere</span>
                  <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
                </Link>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* MISBRUGSBEHANDLING VED DOBBELTDIAGNOSE KRÆVER SAMMENHÆNG */}
      <section className="py-20 sm:py-28 bg-[#F8FAFC] border-b border-[#1C1C1E]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            
            {/* Left Column: Heading and copy */}
            <div className="lg:col-span-7 space-y-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-8 h-[1px] bg-[#3885B8]" />
                <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#3885B8]">
                  DOBBELTDIAGNOSE & KOMORBIDITET
                </span>
              </div>

              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#1C1C1E] tracking-tight mb-4">
                Misbrugsbehandling ved dobbeltdiagnose kræver sammenhæng
              </h2>

              <div className="space-y-4 text-base text-[#1C1C1E]/80 leading-relaxed">
                <p>
                  Ved en dobbeltdiagnose optræder misbrug sammen med en psykisk lidelse. De to problemstillinger kan påvirke hinanden, og derfor bør de ses i sammenhæng. Vores misbrugsbehandling ved{' '}
                  <Link
                    to="/dobbeltdiagnose"
                    className="font-medium text-[#3885B8] underline decoration-[#3885B8]/40 hover:decoration-[#3885B8] hover:text-[#2A6E9B] transition-colors"
                  >
                    dobbeltdiagnose
                  </Link>{' '}
                  bygger på en helhedsorienteret indsats, der tager højde for både misbruget og den psykiske situation.
                </p>
                <p>
                  Vi arbejder også med dobbeltproblematikker, hvor misbruget optræder sammen med kriminalitet, ludomani eller mistanke om psykiatriske udfordringer. Her tilrettelægges forløbet ud fra det samlede støttebehov, så indsatsen bliver tydelig, relevant og realistisk for den enkelte.
                </p>
              </div>

              {/* Key points in grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4">
                <div className="flex items-start gap-3 p-4 bg-white border border-[#1C1C1E]/10 rounded-none">
                  <div className="w-8 h-8 rounded-none bg-[#3885B8]/10 text-[#3885B8] flex items-center justify-center shrink-0 text-sm">
                    <i className="fa-solid fa-brain" aria-hidden="true" />
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-[#1C1C1E]">Psykisk sårbarhed & misbrug</h4>
                    <p className="text-xs text-[#1C1C1E]/70 mt-0.5">Samlet udredning og koordineret indsats uden at isolere problemerne.</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-4 bg-white border border-[#1C1C1E]/10 rounded-none">
                  <div className="w-8 h-8 rounded-none bg-[#3885B8]/10 text-[#3885B8] flex items-center justify-center shrink-0 text-sm">
                    <i className="fa-solid fa-diagram-project" aria-hidden="true" />
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-[#1C1C1E]">Helhedsorienteret Cenaps</h4>
                    <p className="text-xs text-[#1C1C1E]/70 mt-0.5">Anerkendt kognitiv model tilpasset den enkeltes funktionsniveau.</p>
                  </div>
                </div>
              </div>

              <div className="pt-2">
                <Link
                  to="/dobbeltdiagnose"
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-[#3885B8] hover:bg-[#2A6E9B] text-white font-bold text-sm transition-colors shadow-md"
                >
                  <span>Læs mere om behandling af dobbeltdiagnose</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                </Link>
              </div>
            </div>

            {/* Right Column: Visual photo with caption */}
            <div className="lg:col-span-5">
              <div className="rounded-none overflow-hidden border border-[#1C1C1E]/10 shadow-xl bg-white">
                <img
                  src="https://assets.auxodev.dk/assets/caa9ce5a-9b51-4c31-bd1f-3a73d776d9c2/mand-i-samtale-i-laenestol-under-ovenlys.jpg"
                  alt="Mand i samtale siddende i en lænestol under et ovenlysvindue"
                  className="w-full h-80 sm:h-96 object-cover rounded-none"
                />
                <div className="p-4 bg-white rounded-none border-t border-[#1C1C1E]/10">
                  <div className="text-xs text-[#1C1C1E]/70">
                    Vi har mere end 20 års erfaring med differentierede forløb for borgere fra hele landet, herunder tæt samarbejde med kommuner efter SEL § 101 og § 141.
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* MIDTGAARDENS FYSISKE RAMMER */}
      <section className="py-20 sm:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            
            {/* Left Column: Two separate vertical images side by side in asymmetrical arrangement */}
            <div className="lg:col-span-6 flex gap-4 sm:gap-6 items-start">
              {/* First Image: same width */}
              <div className="w-1/2 shrink-0">
                <img
                  src={homeImages[0]}
                  alt="Hvidmalet bygning med græsplæne og hegn"
                  loading="lazy"
                  className="w-full h-[450px] sm:h-[550px] object-cover rounded-none shadow-md"
                />
              </div>

              {/* Second Image: same width */}
              <div className="w-1/2 shrink-0 mt-8 sm:mt-12">
                <img
                  src="https://assets.auxodev.dk/assets/caa9ce5a-9b51-4c31-bd1f-3a73d776d9c2/ung-mand-sigter-med-koeen-paa-poolbord.jpg"
                  alt="Ung mand der sigter med køen på et grønt poolbord i aktivitetsrummet"
                  loading="lazy"
                  className="w-full h-[450px] sm:h-[550px] object-cover rounded-none shadow-md"
                />
              </div>
            </div>

            {/* Right Column: Compact left-aligned text block */}
            <div className="lg:col-span-6 space-y-6">
              
              {/* Uppercase eyebrow label with thin horizontal line to left */}
              <div className="flex items-center gap-3">
                <div className="w-8 h-[1px] bg-[#3885B8]" />
                <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#3885B8]">
                  MIDTGAARDENS FYSISKE RAMMER
                </span>
              </div>

              {/* Refined editorial heading over multiple lines */}
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#1C1C1E] tracking-tight mb-4">
                Trygge og hjemlige omgivelser i Sejstrup og Esbjerg
              </h2>

              {/* Lead paragraph */}
              <p className="text-base sm:text-lg text-[#1C1C1E] font-medium leading-relaxed">
                Midtgaarden i Sejstrup ligger i en moderniseret trelænget landejendom med store udenomsarealer, fællesrum, tv-stuer og sportsfaciliteter.
              </p>

              {/* Supporting body paragraphs */}
              <p className="text-sm text-[#1C1C1E]/75 leading-relaxed">
                I Esbjerg tilbyder Halvvejshuset en rolig villa med enkeltværelser, tæt på uddannelsesinstitutioner og arbejdspladser. Begge afdelinger danner en tryg ramme om borgerens hverdag.
              </p>

              <div className="pt-2">
                <Link
                  to="/midtgaarden"
                  className="inline-flex items-center gap-2 text-sm font-bold text-[#3885B8] hover:underline"
                >
                  <span>Læs mere om vores afdelinger</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                </Link>
              </div>
            </div>

          </div>

        </div>
      </section>

      {/* FÅ EN AFKLARENDE SAMTALE OM BEHANDLING / KONTAKT WEBFORM SECTION */}
      <section className="relative py-20 sm:py-28 bg-[#1C1C1E] text-white overflow-hidden">
        {/* Full-bleed background image with dark overlay */}
        <img
          src="https://assets.auxodev.dk/assets/caa9ce5a-9b51-4c31-bd1f-3a73d776d9c2/loungeomraade-med-laenestole-under-skraavaegge.jpg"
          alt="Loungeområde med sorte lænestole og sofabord under skråvægge med synlige bjælker"
          className="absolute inset-0 w-full h-full object-cover opacity-60 rounded-none"
        />
        <div className="absolute inset-0 bg-[#1C1C1E]/60 backdrop-blur-xs" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            
            {/* Left Column: Heading, text, buttons */}
            <div className="lg:col-span-6 space-y-6">
              <span className="text-xs uppercase tracking-widest font-semibold text-[#3885B8]">
                AFKLARENDE SAMTALE & KONTAKT
              </span>

              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white tracking-tight mb-4">
                Få en afklarende samtale om behandling
              </h2>

              <p className="text-base sm:text-lg text-white/90 leading-relaxed font-normal">
                Har du brug for at få hjælp til at komme ud af misbrug, eller er du i kontakt med en, som har det? Så er du meget velkommen til at ringe hertil på telefon{' '}
                <a
                  href={COMPANY_INFO.phoneTel}
                  className="text-white font-medium underline underline-offset-2 hover:text-[#3885B8] transition-colors whitespace-nowrap"
                >
                  {COMPANY_INFO.phone}
                </a>{' '}
                eller sende en e-mail til{' '}
                <a
                  href={COMPANY_INFO.emailMailto}
                  className="text-white font-medium underline underline-offset-2 hover:text-[#3885B8] transition-colors whitespace-nowrap"
                >
                  {COMPANY_INFO.email}
                </a>{' '}
                og høre mere om døgnbehandling og Midtgaarden generelt.
              </p>

              <div className="pt-4 flex flex-wrap items-center gap-4">
                <a
                  href={COMPANY_INFO.phoneTel}
                  className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-[#3885B8] hover:bg-[#2A6E9B] text-white font-semibold text-sm transition-all shadow-md"
                >
                  <i className="fa-solid fa-phone" aria-hidden="true" />
                  <span>Ring: {COMPANY_INFO.phone}</span>
                </a>

                <a
                  href={COMPANY_INFO.emailMailto}
                  className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full border border-white/30 text-white/90 font-semibold text-sm hover:border-white hover:text-white transition-all backdrop-blur-xs"
                >
                  <i className="fa-solid fa-envelope" aria-hidden="true" />
                  <span>{COMPANY_INFO.email}</span>
                </a>
              </div>
            </div>

            {/* Right Column: Webform structured vertically */}
            <div className="lg:col-span-6 bg-white/5 p-8 rounded-none border border-white/15 backdrop-blur-md shadow-2xl">
              <h3 className="text-xl font-bold text-white mb-6">Send en besked</h3>

              {formSubmitted ? (
                <div className="p-6 bg-[#3885B8]/20 border border-[#3885B8] rounded-none text-center space-y-3">
                  <i className="fa-solid fa-circle-check text-3xl text-[#3885B8]" aria-hidden="true" />
                  <h4 className="text-lg font-bold text-white">Mange tak for din henvendelse</h4>
                  <p className="text-xs text-white/80">Vi har modtaget din besked og vender tilbage hurtigst muligt.</p>
                </div>
              ) : (
                <form onSubmit={handleFormSubmit} className="space-y-6">
                  <input type="hidden" name="_form" value="kontakt" />
                  
                  {/* Honeypot Antispam Field */}
                  <div className="sr-only" aria-hidden="true">
                    <label htmlFor="website_url">Website URL</label>
                    <input type="text" id="website_url" name="website_url" tabIndex="-1" autoComplete="off" />
                  </div>

                  {/* Top row 50/50 grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="block text-xs font-semibold uppercase tracking-wider text-white/70 mb-1">
                        Navn *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        placeholder="Dit navn"
                        className="w-full bg-transparent border-b border-white/30 focus:border-white py-2 text-sm text-white placeholder:text-white/30 focus:outline-none transition-colors"
                      />
                    </div>

                    <div>
                      <label htmlFor="phone" className="block text-xs font-semibold uppercase tracking-wider text-white/70 mb-1">
                        Telefonnummer *
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        required
                        placeholder="Dine 8 cifre"
                        className="w-full bg-transparent border-b border-white/30 focus:border-white py-2 text-sm text-white placeholder:text-white/30 focus:outline-none transition-colors"
                      />
                    </div>
                  </div>

                  {/* Email field */}
                  <div>
                    <label htmlFor="email" className="block text-xs font-semibold uppercase tracking-wider text-white/70 mb-1">
                      E-mailadresse *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      placeholder="din@email.dk"
                      className="w-full bg-transparent border-b border-white/30 focus:border-white py-2 text-sm text-white placeholder:text-white/30 focus:outline-none transition-colors"
                    />
                  </div>

                  {/* Description / Message field */}
                  <div>
                    <label htmlFor="message" className="block text-xs font-semibold uppercase tracking-wider text-white/70 mb-1">
                      Beskrivelse *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows="4"
                      required
                      placeholder="Skriv din besked eller spørgsmål her..."
                      className="w-full bg-transparent border-b border-white/30 focus:border-white py-2 text-sm text-white placeholder:text-white/30 focus:outline-none transition-colors resize-none"
                    ></textarea>
                  </div>

                  {formError && (
                    <p className="text-xs font-semibold text-rose-400">{formError}</p>
                  )}

                  {/* Full-width pill-shaped primary CTA button */}
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full py-4 px-6 rounded-full bg-[#3885B8] hover:bg-[#2A6E9B] text-white font-bold text-sm tracking-wider uppercase transition-colors shadow-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#3885B8] disabled:opacity-50 cursor-pointer"
                  >
                    {submitting ? 'Sender henvendelse...' : 'Send henvendelse'}
                  </button>
                </form>
              )}
            </div>

          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      <GalleryModal
        images={homeImages}
        isOpen={galleryOpen}
        initialIndex={galleryIndex}
        onClose={() => setGalleryOpen(false)}
      />
    </>
  );
}
